import { useEffect, useLayoutEffect, useState } from "react";
import { useReducedMotion } from "motion/react";
import GlowCursor from "../../components/GlowCursor";
import { THEME_EVENT, getResolvedTheme } from "../../utility/theme";

/* An easter egg on the employee dashboard: a band over the bottom 24% of the
   screen (everything below the 76% line) where the pointer leaves a trail of
   glowing light. GlowCursor only draws while the pointer moves inside its
   box, so the band is invisible until someone happens to drag through it.

   The band is fixed to the viewport, so it is reachable however long the page
   is, and lines up with the main panel (.app-inset) rather than the whole
   window, so the sidebar never lights up. It lets clicks through to whatever
   is underneath.

   GlowCursor parses its colour on a canvas, which cannot resolve var(), so
   the accent is read off :root here and again whenever the theme flips; the
   light theme gets GlowCursor's ink look instead of the screen-blended glow,
   which would vanish on white. Reduced motion gets nothing at all. */
const TRIGGER_FROM = 0.76;

const readLook = () => ({
  color:
    getComputedStyle(document.documentElement)
      .getPropertyValue("--app-accent-bright")
      .trim() || "#c10007",
  theme: getResolvedTheme(),
});

const insetBounds = () => {
  const inset = document.querySelector(".app-inset");
  if (!inset) return { left: 0, width: window.innerWidth };
  const rect = inset.getBoundingClientRect();
  return { left: rect.left, width: rect.width };
};

const GlowCursorEgg = () => {
  const reduce = useReducedMotion();
  const [look, setLook] = useState(readLook);
  const [bounds, setBounds] = useState(insetBounds);

  useEffect(() => {
    const onThemeChange = () => setLook(readLook());
    window.addEventListener(THEME_EVENT, onThemeChange);
    return () => window.removeEventListener(THEME_EVENT, onThemeChange);
  }, []);

  // follow the panel as the window resizes or the sidebar collapses
  useLayoutEffect(() => {
    const update = () => setBounds(insetBounds());
    update();
    window.addEventListener("resize", update);
    const inset = document.querySelector(".app-inset");
    const observer =
      inset && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(update)
        : null;
    observer?.observe(inset);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, []);

  if (reduce) return null;

  return (
    <GlowCursor
      aria-hidden="true"
      color={look.color}
      theme={look.theme}
      intensity={1.3}
      trailWidth={3}
      linger={1.4}
      glow={1.2}
      clickBurst
      style={{
        position: "fixed",
        left: bounds.left,
        width: bounds.width,
        top: `${TRIGGER_FROM * 100}vh`,
        bottom: 0,
        height: "auto",
        zIndex: 20,
        pointerEvents: "none",
      }}
    />
  );
};

export default GlowCursorEgg;
